import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  useSEO(pageConfigs.contact);

  const handleCallPhone = () => {
    window.location.href = 'tel:+919711834001';
  };

  const handleSendEmail = () => {
    window.location.href = 'mailto:info@3arrowsmedia.com';
  };

  const contactMethods = [
    {
      title: "Phone",
      description: "Speak directly with our team",
      icon: <PhoneIcon className="w-8 h-8" />,
      primary: "+919711834001",
      secondary: "Mon-Fri 9AM-6PM EST"
    },
    {
      title: "Email",
      description: "Send us a detailed message",
      icon: <EnvelopeIcon className="w-8 h-8" />,
      primary: "info@3arrowsmedia.com",
      secondary: "We respond within 24 hours"
    },
    {
      title: "Visit",
      description: "Visit our Office",
      icon: <MapPinIcon className="w-8 h-8" />,
      primary: "See below",
      secondary: "Available during business hours"
    }
  ];

  const services = [
    "Lead Generation for Local Business",
    "Mobile Development",
    "Website Development", 
    "Digital Marketing",
    "Social Media Management",
    "Meta Ads Management",
    "Google Account Management",
    "Amazon Advertising Services"
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.contact} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
              Let's Start Your 
              <span className="text-blue-600"> Success Story</span>
            </h1>
            
            <p className="text-lg text-slate-700 mb-12 leading-relaxed">
              Ready to transform your business with our digital marketing expertise? 
              Get in touch with our team and let's discuss how we can help you achieve your goals.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="bg-blue-100 p-3 rounded-lg w-fit mx-auto mb-6">
                    <div className="text-blue-600">{method.icon}</div>
                  </div>
                  <h3 className="text-xl font-display font-bold text-slate-900 mb-2">{method.title}</h3>
                  <p className="text-slate-600 mb-4">{method.description}</p>
                  <div className="text-lg font-semibold text-slate-900 mb-1">{method.primary}</div>
                  <div className="text-sm text-slate-500">{method.secondary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                Send Us a Message
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours. 
                We're here to help you grow your business.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Free Consultation</h4>
                    <p className="text-slate-600">Get expert advice on your project</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Custom Quote</h4>
                    <p className="text-slate-600">Receive a detailed project estimate</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Quick Response</h4>
                    <p className="text-slate-600">We respond within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-8">
              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">First Name *</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Last Name *</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Email *</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@company.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Phone</label>
                  <input 
                    type="tel" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(123) 456-7890"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Company</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your Company Name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Service Interest *</label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Message *</label>
                  <textarea 
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Tell us about your project and goals..."
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Office Hours Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                Office Hours
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We're here to help you during business hours. Outside of these hours, 
                feel free to leave us a message and we'll get back to you as soon as possible.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-slate-900">Monday - Friday</span>
                  <span className="text-slate-600">9:00 AM - 6:00 PM EST</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-slate-900">Saturday</span>
                  <span className="text-slate-600">10:00 AM - 4:00 PM EST</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="font-semibold text-slate-900">Sunday</span>
                  <span className="text-slate-600">Closed</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center space-x-4 mb-6">
                <MapPinIcon className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-display font-bold text-slate-900">Our Location</h3>
              </div>
              <p className="text-slate-600 mb-6">
                123 Business Street<br />
                Suite 100<br />
                New York, NY 10001<br />
                United States
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="w-5 h-5 text-slate-400" />
                  <span>+919711834001</span>
                </div>
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="w-5 h-5 text-slate-400" />
                  <span>info@3arrowsmedia.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Don't wait to transform your business. Contact us today and let's discuss 
            how we can help you achieve your digital marketing goals.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleCallPhone}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <PhoneIcon className="w-5 h-5" />
              <span>Call +919711834001</span>
            </button>
            <button 
              onClick={handleSendEmail}
              className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <EnvelopeIcon className="w-5 h-5" />
              <span>Send Email</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
